import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { DateRangePicker } from "rsuite";
import { useNarrowWindow } from "../common/useNarrowWindow";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import Header from "../Header/Header";
import { styles } from "../MainPage/MainPage";
import { messages } from "../common/notification";
import { useStoreListRefresh } from "../common/useStoreList";
import { useDashboardStoreDefault } from "../common/useDashboardStore";
import { loadHouseForPicker } from "../DeviceSchedule/api";
import {
  ALLOWS_MANUAL_DEVICES,
  DATE_FORMAT,
  MAX_RANGE_DAYS,
  NEEDS_EQUIPMENT,
  REPORT_TYPE_OPTIONS,
  STORE_LEVEL,
} from "./constants";
import { deviceLabel, downloadCsv, reportFileName, runReport } from "./functions";
import { MultiSelect, selectStyles, storeOptionLabel } from "./selects";
import FilterHeaderCell, { ActiveFilters } from "../common/FilterHeaderCell";
import { filterOptions, matchesColumnFilters } from "../common/columnFilters";
import "./Reports.css";

const { afterToday, allowedMaxDays, combine } = DateRangePicker;

// Days covered by a range, counting both ends.
const rangeDays = (range) =>
  dayjs(range.endDate).startOf("day").diff(dayjs(range.startDate).startOf("day"), "day") + 1;

// A device ID typed in by hand. It has no `device`, so no property either.
const TYPE_FILTER = [{ field: "type", kind: "select" }];

const manualDeviceOption = (id) => ({ value: id, label: id, deviceId: id });

const formatRange = (date) =>
  `${dayjs(date.startDate).format(DATE_FORMAT)} - ${dayjs(date.endDate).format(DATE_FORMAT)}`;

// Builds the CSV on click. The label is updated first and the work deferred a
// tick, so "Preparing..." is on screen while a large file is put together.
function DownloadButton({ report, className, title, children }) {
  const [preparing, setPreparing] = useState(false);

  const download = () => {
    setPreparing(true);
    setTimeout(() => {
      try {
        downloadCsv(report.data, reportFileName(report));
      } catch (error) {
        console.error("CSV download failed", error);
        messages.error("Could not build the file");
      } finally {
        setPreparing(false);
      }
    }, 0);
  };

  return (
    <button type="button" className={className} title={title} onClick={download} disabled={preparing}>
      {preparing ? "Preparing..." : children}
    </button>
  );
}

function ReportAction({ report }) {
  const hasRows = report.data?.length > 0;

  if (report.success === undefined) {
    return (
      <button type="button" className="rp-action" disabled>
        <span className="rp-spinner" aria-hidden="true" />
        Loading...
      </button>
    );
  }

  if (report.success === false) {
    // Equipment Logs keeps the rows it read before the error; they are still
    // worth having, so the failed button downloads them when there are any.
    return hasRows ? (
      <DownloadButton
        report={report}
        className="rp-action rp-action-failed"
        title="Failed partway - downloads the rows read before the error"
      >
        Failed
      </DownloadButton>
    ) : (
      <button type="button" className="rp-action rp-action-failed">
        Failed
      </button>
    );
  }

  if (!hasRows) {
    return (
      <button type="button" className="rp-action" disabled>
        Data Not Available
      </button>
    );
  }

  return (
    <DownloadButton report={report} className="rp-action rp-action-primary">
      Download
    </DownloadButton>
  );
}

function Reports() {
  // The two-month date calendar is about 650px wide; below this it shows one.
  const narrowWindow = useNarrowWindow(1280);
  const housesList = useSelector((state) => state.housesList);
  const houses = useSelector((state) => state.mutipleHouse);
  const reports = useSelector((state) => state.unitReport?.reports ?? {});

  const { refreshing: storesRefreshing, refresh: refreshStoreList } =
    useStoreListRefresh();

  const [selectedProperties, setSelectedProperties] = useState([]);
  const [reportType, setReportType] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(0);

  const propertyOptions = useMemo(
    () =>
      (housesList || []).map((property) => ({
        value: property.houseAccessCode,
        label: property.houseName || property.houseAccessCode || "",
        property,
      })),
    [housesList]
  );

  // Opens on the store picked on the Dashboard.
  useDashboardStoreDefault("reports", propertyOptions, selectedProperties, setSelectedProperties);

  const needsEquipment = NEEDS_EQUIPMENT.includes(reportType?.value);
  const allowsManual = ALLOWS_MANUAL_DEVICES.includes(reportType?.value);
  const storeLevel = STORE_LEVEL.includes(reportType?.value);
  const maxDays = MAX_RANGE_DAYS[reportType?.value];
  // Only device-level reports read a property's assets (its rooms and meters);
  // store-level ones are answered per store, so they skip the asset calls.
  const needsDevices = Boolean(reportType) && !storeLevel;

  // Properties read fresh this visit. The multi-house slice is persisted, so a
  // cached copy can be from an earlier session; each property is re-read the
  // first time a device-level report needs it, and served from the slice after
  // that.
  const freshCodes = useRef(new Set());

  useEffect(() => {
    if (!needsDevices) return;

    for (const option of selectedProperties) {
      if (freshCodes.current.has(option.value)) continue;
      freshCodes.current.add(option.value);

      setLoadingProperties((count) => count + 1);
      loadHouseForPicker(option.property, { force: true })
        .then((result) => {
          if (!result.success) {
            freshCodes.current.delete(option.value);
            messages.error(`${option.label}: ${result.message}`);
          }
        })
        .finally(() => setLoadingProperties((count) => count - 1));
    }
  }, [selectedProperties, needsDevices]);

  // Every EnergiSync meter across the selected properties' rooms.
  const devices = useMemo(() => {
    const list = [];
    for (const option of selectedProperties) {
      const house = houses?.[option.value];
      for (const room of house?.rooms ?? []) {
        for (const device of room.energiSync ?? []) {
          list.push({
            deviceId: device.deviceId,
            assetId: device.assetId,
            name: device.name,
            roomName: room.roomName,
            houseName: option.label,
            houseId: option.value,
          });
        }
      }
    }
    return list;
  }, [selectedProperties, houses]);

  const equipmentOptions = useMemo(
    () => devices.map((device) => ({ value: device.deviceId, label: deviceLabel(device), device })),
    [devices]
  );

  // Equipment from a property that has since been deselected is dropped.
  // Device IDs typed in by hand belong to no property, so they stay.
  useEffect(() => {
    const available = new Set(devices.map((device) => device.deviceId));
    setSelectedEquipment((picked) => {
      const kept = picked.filter((option) => !option.device || available.has(option.value));
      return kept.length === picked.length ? picked : kept;
    });
  }, [devices]);

  // A typed ID that matches a device in the selected properties picks that
  // device, so it keeps its name and site.
  const addTypedDevice = (input) => {
    const id = input.trim();
    if (!id) return;
    const option =
      equipmentOptions.find((existing) => existing.value === id) ?? manualDeviceOption(id);
    setSelectedEquipment((picked) =>
      picked.some((existing) => existing.value === id) ? picked : [...picked, option]
    );
  };

  const getReport = () => {
    if (!reportType || !dateRange.startDate || !dateRange.endDate) {
      messages.error("Select All Values");
      return;
    }
    if (maxDays && rangeDays(dateRange) > maxDays) {
      messages.error(`${reportType.value} allows at most ${maxDays} days`);
      return;
    }
    // Reports that take typed-in device IDs can run without any property.
    if (selectedProperties.length === 0 && !allowsManual) {
      messages.error("Select a property");
      return;
    }
    if (needsDevices && loadingProperties > 0) {
      messages.error("Devices are still loading");
      return;
    }
    if (needsDevices && devices.length === 0 && !allowsManual) {
      messages.error("There is no devices in selected house");
      return;
    }
    if (needsEquipment && selectedEquipment.length === 0) {
      messages.error("Equipment not selected");
      return;
    }

    // Not awaited: the row appears at once and the report fills in behind it,
    // so another can be started straight away.
    runReport(uuidv4(), {
      type: reportType.value,
      dateRange,
      devices: needsEquipment
        ? selectedEquipment.map((option) => option.device ?? { deviceId: option.deviceId })
        : devices,
      properties: selectedProperties.map((option) => ({
        houseId: option.value,
        houseName: option.label,
      })),
    });
  };

  const reportIds = Object.keys(reports);

  // The list's one header filter: the report type, any number picked. Rows
  // keep the number they were given, so a filtered list still matches it.
  const [typeFilter, setTypeFilter] = useState([]);
  const typeOptions = useMemo(() => filterOptions(Object.values(reports), "type"), [reports]);
  const shownReports = reportIds
    .map((id, index) => ({ id, number: index + 1, report: reports[id] }))
    .filter(({ report }) => matchesColumnFilters(report, { type: typeFilter }, TYPE_FILTER));

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div className="rp-page">
          <div className="rp-title">Reports</div>
          <div className="rp-subtitle">
            {selectedProperties.length === 0
              ? "Pick one or more properties to build a report"
              : !needsDevices
                ? `${selectedProperties.length} propert${
                    selectedProperties.length === 1 ? "y" : "ies"
                  } selected`
                : loadingProperties > 0
                  ? "Loading devices..."
                  : `${devices.length} device${devices.length === 1 ? "" : "s"} across ${
                      selectedProperties.length
                    } propert${selectedProperties.length === 1 ? "y" : "ies"}`}
          </div>

          <div className="rp-filters">
            <div className="rp-field">
              <span className="rp-label">Property</span>
              <MultiSelect
                options={propertyOptions}
                value={selectedProperties}
                onChange={setSelectedProperties}
                summary={{ empty: "Select properties...", nouns: "properties" }}
                // The store code is shown before the name (menu and box), but
                // only as display: the option's label stays the bare name,
                // since it is the report's Store Name fallback.
                formatOptionLabel={storeOptionLabel}
                onMenuOpen={refreshStoreList}
                isLoading={storesRefreshing || (needsDevices && loadingProperties > 0)}
                noOptionsMessage={() => "No properties"}
                width="260px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Report Type</span>
              <Select
                options={REPORT_TYPE_OPTIONS}
                value={reportType}
                onChange={(option) => {
                  setReportType(option);
                  setSelectedEquipment([]);
                  // A range picked for an unlimited report may be too long
                  // for this one; clear it rather than keep an invalid range.
                  const limit = MAX_RANGE_DAYS[option?.value];
                  if (
                    limit &&
                    dateRange.startDate &&
                    dateRange.endDate &&
                    rangeDays(dateRange) > limit
                  ) {
                    setDateRange({ startDate: null, endDate: null });
                  }
                }}
                placeholder="Select report..."
                isSearchable={false}
                styles={selectStyles("220px")}
              />
            </div>

            {reportType && (
              <div className="rp-field">
                <span className="rp-label">
                  Select Date{maxDays ? ` (max ${maxDays} days)` : ""}
                </span>
                <DateRangePicker
                  // Keyed on the limit so switching report types rebuilds the
                  // picker with the right disabled dates.
                  key={maxDays || "unlimited"}
                  shouldDisableDate={
                    maxDays ? combine(afterToday(), allowedMaxDays(maxDays)) : afterToday()
                  }
                  ranges={[]}
                  // Lined up with the field's right edge and flipped above it when there
                  // is no room below, so the calendar stays on screen; one month instead of
                  // two on narrow windows.
                  placement="autoVerticalEnd"
                  showOneCalendar={narrowWindow}
                  placeholder={maxDays ? `Up to ${maxDays} days` : "Select Date Range"}
                  format="dd-MM-yyyy"
                  character=" - "
                  value={
                    dateRange.startDate && dateRange.endDate
                      ? [dateRange.startDate, dateRange.endDate]
                      : null
                  }
                  onChange={(range) =>
                    setDateRange(
                      range
                        ? { startDate: range[0], endDate: range[1] }
                        : { startDate: null, endDate: null }
                    )
                  }
                  style={{ width: 240 }}
                />
              </div>
            )}

            {needsEquipment && allowsManual && (
              <div className="rp-field">
                <span className="rp-label">Select Equipment</span>
                <MultiSelect
                  SelectComponent={CreatableSelect}
                  options={equipmentOptions}
                  value={selectedEquipment}
                  onChange={setSelectedEquipment}
                  summary={{ empty: "Select or type a device ID...", nouns: "devices" }}
                  onCreateOption={addTypedDevice}
                  isValidNewOption={(input) => input.trim() !== ""}
                  formatCreateLabel={(input) =>
                    `Add device ID "${input.trim()}" (not in this property)`
                  }
                  noOptionsMessage={() =>
                    selectedProperties.length === 0 ? "Pick a property or type a device ID" : "No devices"
                  }
                  width="260px"
                />
              </div>
            )}

            {needsEquipment && !allowsManual && (
              <div className="rp-field">
                <span className="rp-label">Select Equipment</span>
                <MultiSelect
                  options={equipmentOptions}
                  value={selectedEquipment}
                  onChange={setSelectedEquipment}
                  summary={{ empty: "Select equipment...", nouns: "devices" }}
                  noOptionsMessage={() =>
                    selectedProperties.length === 0 ? "Pick a property first" : "No devices"
                  }
                  width="260px"
                />
              </div>
            )}

            <button type="button" className="rp-get" onClick={getReport}>
              Get Report
            </button>
          </div>

          <ActiveFilters
            specs={TYPE_FILTER}
            labels={{ type: "Type" }}
            filters={{ type: typeFilter }}
            options={{ type: typeOptions }}
            onChange={(field, value) => setTypeFilter(value)}
            onClear={() => setTypeFilter([])}
          />

          <div className="rp-table-wrap rp-table-scroll">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date Range</th>
                  <FilterHeaderCell
                    label="Type"
                    kind="select"
                    value={typeFilter}
                    options={typeOptions}
                    onChange={setTypeFilter}
                  />
                  <th>Percentage</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {shownReports.length === 0 ? (
                  <tr>
                    <td className="rp-empty" colSpan={5}>
                      {reportIds.length === 0
                        ? "Reports you generate appear here."
                        : "No reports of these types."}
                    </td>
                  </tr>
                ) : (
                  shownReports.map(({ id, number, report }) => {
                    // Floored so 100% only shows once every call is in.
                    const percent = report.totalCalls
                      ? Math.floor((report.currentCall / report.totalCalls) * 100)
                      : 0;
                    return (
                      <tr key={id}>
                        <td>{number}</td>
                        <td>{formatRange(report.date)}</td>
                        <td>{report.type}</td>
                        <td>{Math.min(percent, 100)}%</td>
                        <td>
                          <ReportAction report={report} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
